import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import type { CSSProperties } from "react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths, eachDayOfInterval } from "date-fns";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

export type CalendarPosition = "top" | "bottom" | "auto";
export type CalendarMode = "single" | "multi";

export interface CalendarLabels {
  monthNames: string[];
  dayNames: string[];
  today: string;
  clear: string;
}

/** Saisie manuelle dans le pied du popup (recherche critères — hors formulaires). */
export interface CalendarFooterDateInput {
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onCommit: () => void;
}

export interface U2000CalendarPopupProps {
  isOpen: boolean;
  currentDate: Date;
  currentMonth: Date;
  mode: CalendarMode;
  position: CalendarPosition;
  selectedValue?: string;
  selectedValues?: string[];
  labels: CalendarLabels;
  onSelectDate: (date: Date) => void;
  onTodayClick: () => void;
  onClearClick: () => void;
  onMonthChange: (month: Date) => void;
  onClose: () => void;
  footerDateInput?: CalendarFooterDateInput;
  className?: string;
  zIndex?: number;
  /** Coordonnées viewport : popup en `position: fixed` (ex. rendu via portal hors d’un conteneur scroll / overflow). */
  viewportFixed?: { top: number; left: number };
}

export interface U2000CalendarPopupHandle {
  focus: () => void;
}

function isValidDate(d: unknown): d is Date {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

function normalizeToValidDate(d: Date, fallback: Date = new Date()): Date {
  return isValidDate(d) ? d : fallback;
}

/** Valeur formulaire : yyyy-MM-dd ou datetime ISO renvoyé par l’API — évite selectedValue + "T00:00:00" qui casse les ISO. */
function parseFormDateString(value: string | undefined | null): Date | null {
  if (value == null || String(value).trim() === "") return null;
  const s = String(value).trim();
  const ymd = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const local = new Date(`${ymd}T00:00:00`);
    return isValidDate(local) ? local : null;
  }
  const parsed = new Date(s);
  return isValidDate(parsed) ? parsed : null;
}

function toYyyyMmDd(value: string | undefined | null): string | null {
  const d = parseFormDateString(value);
  return d ? format(d, "yyyy-MM-dd") : null;
}

/** Référence stable : ne pas utiliser `selectedValues = []` dans les props (nouveau [] à chaque rendu → boucle useEffect). */
const EMPTY_SELECTED_VALUES: string[] = [];

const U2000CalendarPopup = forwardRef<U2000CalendarPopupHandle, U2000CalendarPopupProps>(
  (
    {
      isOpen,
      currentDate,
      currentMonth,
      mode,
      position,
      selectedValue,
      selectedValues,
      labels,
      onSelectDate,
      onTodayClick,
      onClearClick,
      onMonthChange,
      onClose,
      footerDateInput,
      className = "",
      zIndex = 9999,
      viewportFixed,
    },
    ref
  ) => {
    const resolvedSelectedValues = selectedValues ?? EMPTY_SELECTED_VALUES;
    /** Clé stable pour les deps (évite une nouvelle référence de tableau à chaque rendu). */
    const selectedMultiKey = resolvedSelectedValues.join("\u0000");

    const calendarRef = useRef<HTMLDivElement>(null);
    const [actualPosition, setActualPosition] = useState<"top" | "bottom">("bottom");
    const [focusedDay, setFocusedDay] = useState<Date | null>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (calendarRef.current) {
          const firstFocusableElement = calendarRef.current.querySelector('[tabindex="0"]') as HTMLElement;
          firstFocusableElement?.focus();
        }
      },
    }));

    const safeMonth = normalizeToValidDate(currentMonth);
    const safeToday = normalizeToValidDate(currentDate);
    const startDate = startOfWeek(startOfMonth(safeMonth));
    const endDate = endOfWeek(endOfMonth(safeMonth));
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    // Détection automatique du positionnement (ignoré si viewportFixed : placement géré par le parent)
    useEffect(() => {
      if (viewportFixed) return;
      if (position === "auto" && calendarRef.current) {
        const rect = calendarRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.top;
        const spaceAbove = rect.top;

        // Si pas assez d'espace en bas (350px pour le calendrier), utiliser le haut
        if (spaceBelow < 350 && spaceAbove > 350) {
          setActualPosition("top");
        } else {
          setActualPosition("bottom");
        }
      } else if (position !== "auto") {
        setActualPosition(position);
      }
    }, [position, isOpen, viewportFixed]);

    // Gestion de la navigation clavier
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (!focusedDay || !isValidDate(focusedDay)) return;

        switch (e.key) {
          case "ArrowLeft":
            e.preventDefault();
            setFocusedDay(new Date(focusedDay.getTime() - 24 * 60 * 60 * 1000));
            break;
          case "ArrowRight":
            e.preventDefault();
            setFocusedDay(new Date(focusedDay.getTime() + 24 * 60 * 60 * 1000));
            break;
          case "ArrowUp":
            e.preventDefault();
            setFocusedDay(new Date(focusedDay.getTime() - 7 * 24 * 60 * 60 * 1000));
            break;
          case "ArrowDown":
            e.preventDefault();
            setFocusedDay(new Date(focusedDay.getTime() + 7 * 24 * 60 * 60 * 1000));
            break;
          case "Enter":
            e.preventDefault();
            onSelectDate(focusedDay);
            if (mode === "single") {
              onClose();
            }
            break;
          case "Escape":
            e.preventDefault();
            onClose();
            break;
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, focusedDay, onSelectDate, onClose, mode]);

    const footerInputValue = footerDateInput?.value ?? "";

    // Initialiser focusedDay à l’ouverture ; deps stables (pas de selectedValues en référence mutable seule)
    useEffect(() => {
      if (!isOpen) {
        setFocusedDay(null);
        return;
      }
      if (footerDateInput && toYyyyMmDd(footerInputValue) == null) {
        setFocusedDay(normalizeToValidDate(currentDate));
        return;
      }
      const fallback = normalizeToValidDate(currentDate);
      let next: Date;
      if (footerDateInput && toYyyyMmDd(footerInputValue)) {
        next = parseFormDateString(footerInputValue) ?? fallback;
      } else if (selectedValue) {
        next = parseFormDateString(selectedValue) ?? fallback;
      } else if (resolvedSelectedValues.length > 0) {
        next = parseFormDateString(resolvedSelectedValues[0]) ?? fallback;
      } else {
        next = fallback;
      }
      setFocusedDay((prev) => (prev !== null && prev.getTime() === next.getTime() ? prev : next));
    }, [isOpen, selectedValue, selectedMultiKey, currentDate, footerDateInput, footerInputValue]);

    const selectedSingleYmd = mode === "single" ? toYyyyMmDd(selectedValue) : null;
    const selectedMultiYmd =
      mode === "multi" ? resolvedSelectedValues.map((v) => toYyyyMmDd(v)).filter((x): x is string => x != null) : [];

    /** Recherche critères : la grille suit le champ du pied (vide → aujourd’hui gris ; date valide → contour bleu). */
    const useFooterDrivenSelection = Boolean(footerDateInput);
    const footerInputYmd = useFooterDrivenSelection ? toYyyyMmDd(footerDateInput?.value) : null;
    const effectiveSingleYmd = useFooterDrivenSelection ? footerInputYmd : selectedSingleYmd;

    const hasDateSelection = useFooterDrivenSelection
      ? footerInputYmd != null
      : mode === "single"
        ? selectedSingleYmd != null
        : selectedMultiYmd.length > 0;

    /** Même apparence qu’un jour survolé (fond gris léger). */
    const dayHoverLikeClasses = "bg-gray-100 text-gray-900 hover:bg-gray-100 hover:text-gray-900";
    const dayNormalClasses =
      "text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus-visible:bg-gray-100 focus-visible:text-gray-900";
    /** Date saisie / sélectionnée (recherche) : contour bleu + fond clair. */
    const daySelectedOutlineClasses = "bg-blue-50 text-blue-700 border-2 border-blue-300 hover:bg-blue-100";

    const getPositionClasses = () => {
      const baseClasses = "absolute left-0 w-80 bg-white rounded-xl border border-gray-200 shadow-xl transition-all duration-200 ease-out backdrop-blur-xs";

      if (actualPosition === "top") {
        return `${baseClasses} bottom-full mb-2 animate-slide-up`;
      }
      return `${baseClasses} top-full mt-2 animate-slide-down`;
    };

    const getViewportFixedClasses = () =>
      "fixed w-80 bg-white rounded-xl border border-gray-200 shadow-xl transition-all duration-200 ease-out backdrop-blur-xs p-4 animate-slide-down";

    const isDateSelected = (day: Date): boolean => {
      const dayStr = format(day, "yyyy-MM-dd");
      if (mode === "single") {
        return effectiveSingleYmd != null && effectiveSingleYmd === dayStr;
      }
      return selectedMultiYmd.includes(dayStr);
    };

    const isDayFocused = (day: Date): boolean => {
      return isValidDate(focusedDay) && format(day, "yyyyMMdd") === format(focusedDay, "yyyyMMdd");
    };

    const getDayClasses = (day: Date): string => {
      const inMonth = format(day, "MM") === format(safeMonth, "MM");
      const isToday = format(day, "yyyyMMdd") === format(safeToday, "yyyyMMdd");
      const baseClasses =
        "w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 select-none focus:outline-hidden focus-visible:outline-hidden";

      // Jour en dehors du mois actuel
      if (!inMonth) {
        return `${baseClasses} text-gray-300 hover:text-gray-400 hover:bg-gray-50`;
      }

      // Jour sélectionné (recherche : contour ; formulaires : plein bleu)
      if (isDateSelected(day)) {
        return useFooterDrivenSelection
          ? `${baseClasses} ${daySelectedOutlineClasses}`
          : `${baseClasses} bg-blue-600 text-white hover:bg-blue-700 shadow-md`;
      }

      // Focus clavier = même rendu que le survol souris
      if (isDayFocused(day)) {
        return `${baseClasses} ${dayHoverLikeClasses}`;
      }

      // Champ pied vide : aujourd’hui = gris (comme survol)
      if (useFooterDrivenSelection && !hasDateSelection && isToday) {
        return `${baseClasses} ${dayHoverLikeClasses}`;
      }

      // Formulaires : aujourd’hui gris si aucune sélection
      if (!useFooterDrivenSelection && isToday && !hasDateSelection) {
        return `${baseClasses} ${dayHoverLikeClasses}`;
      }

      return `${baseClasses} ${dayNormalClasses}`;
    };

    if (!isOpen) {
      return null;
    }

    const panelClass = viewportFixed
      ? `${getViewportFixedClasses()} ${className} ${isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"}`
      : `${getPositionClasses()} p-4 ${className} ${isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"}`;

    const panelStyle: CSSProperties = viewportFixed
      ? { zIndex, top: viewportFixed.top, left: viewportFixed.left }
      : { zIndex };

    return (
      <div
        ref={calendarRef}
        className={panelClass}
        style={panelStyle}
        onMouseDown={(e) => {
          // Ne pas bloquer le focus sur les champs de saisie du pied (recherche critères).
          const target = e.target as HTMLElement;
          if (target.closest("input, textarea, select")) {
            return;
          }
          e.preventDefault();
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Sélecteur de date"
      >
        {/* Header avec navigation */}
        <div className="flex items-center justify-between mb-4 px-1">
          <button
            onClick={() => onMonthChange(subMonths(safeMonth, 1))}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-150 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            tabIndex={0}
            aria-label="Mois précédent"
          >
            <FaChevronLeft className="w-3 h-3" />
          </button>

          <h2 className="text-sm font-semibold text-gray-800 select-none">
            {labels.monthNames[safeMonth.getMonth()]}&nbsp;
            {format(safeMonth, "yyyy")}
          </h2>

          <button
            onClick={() => onMonthChange(addMonths(safeMonth, 1))}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-150 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            tabIndex={0}
            aria-label="Mois suivant"
          >
            <FaChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Corps du calendrier */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {/* En-têtes des jours de la semaine */}
          {labels.dayNames.map((day) => (
            <div key={day} className="w-10 h-8 flex items-center justify-center text-xs font-medium text-gray-500 select-none">
              {day.substring(0, 2)}
            </div>
          ))}

          {/* Jours du mois */}
          {days.map((day, index) => (
            <button
              key={index}
              className={getDayClasses(day)}
              onClick={() => {
                onSelectDate(day);
                if (mode === "single") {
                  onClose();
                }
              }}
              onFocus={() => setFocusedDay(day)}
              tabIndex={format(day, "MM") === format(safeMonth, "MM") ? 0 : -1}
              aria-label={`${format(day, "d")} ${labels.monthNames[day.getMonth()]} ${format(day, "yyyy")}`}
              disabled={format(day, "MM") !== format(safeMonth, "MM")}
            >
              {format(day, "d")}
            </button>
          ))}
        </div>

        {/* Footer : Aujourd'hui | saisie (recherche) | Effacer */}
        <div
          className={
            footerDateInput
              ? "flex items-stretch gap-1.5 border-t border-gray-200 pt-3"
              : "flex items-center justify-between border-t border-gray-200 pt-3"
          }
        >
          <button
            type="button"
            className={`shrink-0 rounded-lg bg-blue-50 px-2.5 py-2 text-xs font-medium text-blue-600 hover:bg-blue-100 hover:text-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 select-none ${
              footerDateInput ? "" : "px-3 text-sm"
            }`}
            onClick={onTodayClick}
            tabIndex={0}
            aria-label="Sélectionner aujourd'hui"
          >
            {labels.today}
          </button>

          {footerDateInput && (
            <input
              type="text"
              value={footerDateInput.value}
              disabled={footerDateInput.disabled}
              placeholder={footerDateInput.placeholder ?? "yyyy-MM-dd"}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2 text-xs text-gray-900 outline-hidden placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => footerDateInput.onChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  footerDateInput.onCommit();
                }
              }}
              onBlur={() => footerDateInput.onCommit()}
              aria-label={footerDateInput.placeholder ?? "yyyy-MM-dd"}
            />
          )}

          <button
            type="button"
            className={`shrink-0 rounded-lg bg-gray-50 px-2.5 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 select-none ${
              footerDateInput ? "" : "px-3 text-sm"
            }`}
            onClick={onClearClick}
            tabIndex={0}
            aria-label="Effacer la sélection"
          >
            {labels.clear}
          </button>
        </div>
      </div>
    );
  }
);

U2000CalendarPopup.displayName = "U2000CalendarPopup";

export default U2000CalendarPopup;
